import { useEffect, useState } from 'react'
import {
  fetchMyGames,
  fetchMyTier,
  launchVTTSession,
  type MyGameSummary,
  type MyTier,
} from '@/services/myGamesService'
import { Button } from '@arcanecircle/ui/grimoire/button'

/**
 * v6 landing — hero-image game cards on a dark "tabletop" surface.
 *
 * Signed-in users see a grid of cards, each surfaced with its
 * `gameImage` (or a deterministic solid-color fallback). The page
 * keeps the AC type stack (Quintessential eyebrow + Balthazar title)
 * but shifts to a darker surface, a subtle dot-grid background, and
 * a gilt/gold accent (vs. the main app's mint) so it reads as
 * "the table", not "the storefront".
 *
 * Unauthenticated visitors keep the centered hero CTA.
 */
export function LandingPage() {
  const [games, setGames] = useState<MyGameSummary[] | null>(null)
  const [gamesError, setGamesError] = useState<string | null>(null)
  const [signInHref, setSignInHref] = useState<string>(
    'https://arcanecircle.games/auth/signin',
  )
  useEffect(() => {
    // After sign-in on apex, NextAuth honors callbackUrl when it points to an
    // allow-listed origin (see auth.redirect in ac-mvp). Pointing it back at
    // the current tabletop URL means the user lands on the page they started
    // from instead of the apex dashboard.
    const cb = encodeURIComponent(window.location.href)
    setSignInHref(`https://arcanecircle.games/auth/signin?callbackUrl=${cb}`)
  }, [])
  const [gamesLoading, setGamesLoading] = useState(true)
  const [launchingGameId, setLaunchingGameId] = useState<string | null>(null)
  const [launchError, setLaunchError] = useState<string | null>(null)
  // `null` while the tier request is pending; the resolved object
  // afterwards. `tierUnauth` flags the 401 case so we can render the
  // unauth CTA without also rendering the "VTT in private alpha"
  // panel (a user who isn't signed in shouldn't see both).
  const [tier, setTier] = useState<MyTier | null>(null)
  const [tierUnauth, setTierUnauth] = useState(false)
  const [tierLoading, setTierLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    Promise.all([fetchMyGames(), fetchMyTier()]).then(([gamesResult, tierResult]) => {
      if (cancelled) return
      if (gamesResult.ok) {
        setGames(gamesResult.games)
        setGamesError(null)
      } else {
        setGames(null)
        setGamesError(gamesResult.error)
      }
      setGamesLoading(false)

      if (tierResult.ok) {
        setTier(tierResult.tier)
        setTierUnauth(false)
      } else {
        setTier(null)
        setTierUnauth(tierResult.status === 401)
      }
      setTierLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const handleLaunch = async (game: MyGameSummary) => {
    const sessionId = game.liveSession?.id ?? game.nextSession?.id
    if (!sessionId) return
    setLaunchError(null)
    setLaunchingGameId(game.gameId)
    const result = await launchVTTSession(sessionId)
    if (result.ok) {
      window.location.href = result.url
    } else {
      setLaunchError(`${game.title}: ${result.error}`)
      setLaunchingGameId(null)
    }
  }

  // Loading: both requests outstanding.
  const isLoadingAny = gamesLoading || tierLoading
  // Treat the tier endpoint as the source of truth for "signed in":
  // games can come back 200 with an empty array for an authed user
  // who has no scheduled sessions, but tier always returns 401 when
  // the cookie isn't valid. tierUnauth carries that signal cleanly.
  const isUnauthenticated = !isLoadingAny && (tierUnauth || (!tier && !games))
  // Authenticated but not on the whitelist.
  const isTierBlocked = !isLoadingAny && tier != null && !tier.vttAllowed
  // Authenticated AND on the whitelist AND has at least one VTT-
  // enabled game with scheduled / active sessions.
  const isSignedInWithGames =
    !isLoadingAny &&
    tier != null &&
    tier.vttAllowed &&
    games != null &&
    games.length > 0

  return (
    <div
      style={{
        minHeight: '100vh',
        // Surface-0 (deeper than the main app's dark-primary) +
        // dot-grid texture = "graph paper / table felt" treatment.
        background: `
          radial-gradient(hsl(var(--ac-gm-gold) / 0.045) 1px, transparent 1px),
          radial-gradient(ellipse 80% 50% at 50% 0%, hsl(var(--ac-mint-primary) / 0.04), transparent 70%),
          hsl(var(--ac-surface-0))
        `,
        backgroundSize: '28px 28px, 100% 100%, 100% 100%',
        backgroundPosition: '0 0, 0 0, 0 0',
        color: 'hsl(var(--ac-text-light))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '1.5rem 1.5rem 3rem',
      }}
    >
      <main
        style={{
          maxWidth: isSignedInWithGames ? 1200 : 720,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
        }}
      >
        {isSignedInWithGames ? <BrandStrip /> : <CenteredHero />}

        {/* My Games — image-heavy grid (gated on tier whitelist) */}
        {isSignedInWithGames && games && (
          <GamesGrid
            games={games}
            launchingGameId={launchingGameId}
            launchError={launchError}
            onLaunch={handleLaunch}
          />
        )}

        {/* Tier-gated: signed in but not on the VTT whitelist */}
        {isTierBlocked && <TierGatedPanel />}

        {/* Empty state — authenticated, whitelisted, zero games */}
        {!isLoadingAny &&
          tier != null &&
          tier.vttAllowed &&
          games != null &&
          games.length === 0 && (
            <section
              className="ac-card-hero"
              style={{
                padding: '2rem',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
              }}
            >
              <div className="font-serif-ac" style={{ fontSize: '1.25rem' }}>
                No games yet
              </div>
              <p
                className="ac-help"
                style={{
                  fontSize: '0.8125rem',
                  maxWidth: 480,
                  margin: '0 auto',
                }}
              >
                Find a game on Arcane Circle and apply, or run your own — once
                your booking is approved (or your campaign has scheduled
                sessions), it'll appear here.
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  justifyContent: 'center',
                  marginTop: '0.5rem',
                }}
              >
                <Button asChild size="sm">
                  <a
                    href="https://arcanecircle.games/games"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    Browse games
                  </a>
                </Button>
              </div>
            </section>
          )}

        {/* Loading */}
        {isLoadingAny && (
          <div
            className="ac-help"
            style={{ textAlign: 'center', padding: '2rem 0' }}
          >
            Loading your games…
          </div>
        )}

        {/* Unauthenticated / network failure */}
        {isUnauthenticated && (
          <section
            className="ac-card-hero"
            style={{
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              textAlign: 'center',
            }}
          >
            <div
              className="font-script"
              style={{
                fontSize: '0.625rem',
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                color: 'hsl(var(--ac-mint-primary))',
              }}
            >
              Sign in to continue
            </div>
            <p
              style={{
                fontSize: '0.9375rem',
                color: 'hsl(var(--ac-text-muted))',
                lineHeight: 1.6,
                maxWidth: 480,
                margin: '0 auto',
              }}
            >
              Sign in to Arcane Circle to see the games you're running or
              playing in — and launch their sessions from here.
            </p>
            {gamesError && gamesError !== 'Not signed in' && (
              <div
                className="ac-help"
                style={{
                  color: 'hsl(var(--ac-warning))',
                  fontSize: '0.6875rem',
                }}
              >
                Couldn't reach the platform ({gamesError}). Check your
                connection.
              </div>
            )}
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'center',
                marginTop: '0.5rem',
                flexWrap: 'wrap',
              }}
            >
              <Button asChild size="sm">
                <a
                  href={signInHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  Sign in to Arcane Circle
                </a>
              </Button>
              <Button asChild variant="outline" size="sm">
                <a
                  href="https://discord.gg/aZZz6VekQR"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  Join our Discord
                </a>
              </Button>
            </div>
          </section>
        )}

        <footer
          style={{
            fontSize: '0.6875rem',
            color: 'hsl(var(--ac-text-muted))',
            textAlign: 'center',
          }}
        >
          © {new Date().getFullYear()} Arcane Circle Games
        </footer>
      </main>
    </div>
  )
}

/**
 * Tier-gated panel for users who are signed in but don't have VTT
 * access (free tier, no active subscription). Explains WHY they're
 * blocked and points them at the subscription upgrade — VTT access
 * is included with any paid plan, so this is a paywall, not a
 * curated wait-list.
 */
function TierGatedPanel() {
  return (
    <section
      className="ac-card-hero"
      style={{
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        textAlign: 'center',
      }}
    >
      <div
        className="font-script"
        style={{
          fontSize: '0.625rem',
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'hsl(var(--ac-gm-gold))',
        }}
      >
        VTT Beta
      </div>
      <h2
        className="font-serif-ac"
        style={{
          fontSize: '1.5rem',
          lineHeight: 1.15,
          margin: 0,
        }}
      >
        The Arcane Circle VTT is available to subscribers
      </h2>
      <p
        style={{
          fontSize: '0.875rem',
          color: 'hsl(var(--ac-text-muted))',
          lineHeight: 1.6,
          maxWidth: 480,
          margin: '0 auto',
        }}
      >
        VTT access is included with every paid plan — Apprentice,
        Wizard, and Archmage — and for our alpha testers. Your account
        isn't on a paid plan yet. Upgrade to jump straight into the
        beta.
      </p>
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'center',
          marginTop: '0.5rem',
          flexWrap: 'wrap',
        }}
      >
        <Button asChild size="sm">
          <a
            href="https://arcanecircle.games/dashboard/settings/subscription"
            style={{ textDecoration: 'none' }}
          >
            Upgrade to unlock
          </a>
        </Button>
        <Button asChild variant="outline" size="sm">
          <a
            href="https://arcanecircle.games"
            style={{ textDecoration: 'none' }}
          >
            Back to Arcane Circle
          </a>
        </Button>
      </div>
    </section>
  )
}

/**
 * Slim brand strip for signed-in users — wordmark left, return-to-
 * platform link right. Wordmark uses Quintessential with a `· VTT`
 * small-caps suffix as a typographic mark of "you're somewhere
 * different from the main site."
 */
function BrandStrip() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid hsl(var(--ac-mint-primary) / 0.08)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <img
          src="/ACLogo_HexCircle.png"
          alt="Arcane Circle"
          style={{ height: 24, width: 'auto', display: 'block' }}
        />
        <span
          className="font-script"
          style={{
            fontSize: '0.875rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'hsl(var(--ac-gm-gold))',
          }}
        >
          Virtual Tabletop
        </span>
      </div>
      <a
        href="https://arcanecircle.games"
        style={{
          fontSize: '0.6875rem',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'hsl(var(--ac-text-muted))',
          textDecoration: 'none',
          transition: 'color var(--ac-dur-fast) var(--ac-ease-out)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'hsl(var(--ac-mint-primary))'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'hsl(var(--ac-text-muted))'
        }}
      >
        ← arcanecircle.games
      </a>
    </div>
  )
}

/**
 * Centered hero kept for unauthenticated / zero-games / loading
 * states — same type stack as before, sized down a notch so it
 * doesn't dominate the new dark surface.
 */
function CenteredHero() {
  return (
    <div style={{ textAlign: 'center', paddingTop: '2rem' }}>
      <div
        className="font-script"
        style={{
          fontSize: '0.6875rem',
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'hsl(var(--ac-mint-primary))',
          marginBottom: '0.75rem',
        }}
      >
        Arcane Circle
      </div>
      <h1
        className="font-serif-ac"
        style={{
          fontSize: 'clamp(2rem, 4vw, 2.75rem)',
          lineHeight: 1.05,
        }}
      >
        Virtual Tabletop
      </h1>
    </div>
  )
}

interface GamesGridProps {
  games: MyGameSummary[]
  launchingGameId: string | null
  launchError: string | null
  onLaunch: (game: MyGameSummary) => void
}

function GamesGrid({
  games,
  launchingGameId,
  launchError,
  onLaunch,
}: GamesGridProps) {
  return (
    <section
      style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
    >
      <header
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem',
        }}
      >
        <div
          className="font-script"
          style={{
            fontSize: '0.6875rem',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'hsl(var(--ac-gm-gold))',
          }}
        >
          Your games
        </div>
        <h2
          className="font-serif-ac"
          style={{
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            lineHeight: 1.1,
            margin: 0,
          }}
        >
          Launch a session
        </h2>
      </header>

      {launchError && (
        <div
          style={{
            padding: '0.625rem 0.75rem',
            background: 'hsl(var(--ac-danger) / 0.15)',
            border: '1px solid hsl(var(--ac-danger) / 0.4)',
            color: 'hsl(var(--ac-danger))',
            borderRadius: 'var(--ac-radius)',
            fontSize: '0.75rem',
          }}
        >
          {launchError}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '1.25rem',
        }}
      >
        {games.map((game) => (
          <GameCard
            key={game.gameId}
            game={game}
            launching={launchingGameId === game.gameId}
            disabled={launchingGameId != null && launchingGameId !== game.gameId}
            onLaunch={() => onLaunch(game)}
          />
        ))}
      </div>
    </section>
  )
}

interface GameCardProps {
  game: MyGameSummary
  launching: boolean
  disabled: boolean
  onLaunch: () => void
}

function GameCard({ game, launching, disabled, onLaunch }: GameCardProps) {
  // Campaign isn't configured for the AC VTT (its GM uses Foundry /
  // Roll20 / etc.). Surfaced for context but rendered greyed-out and
  // unlaunchable — the CTA explains why rather than offering a launch.
  const vttDisabled = !game.vttEnabled
  const canLaunch = !vttDisabled && !!(game.liveSession ?? game.nextSession)
  const isLive = !vttDisabled && !!game.liveSession
  const isGM = game.role === 'gm'
  const fallbackColor = gameIdToFallbackColor(game.gameId)

  // Border treatment: live → mint glow (it's the "something's happening
  // right now" signal). Idle → thin gilt inset edge that reads as a
  // card on felt, not a Material drop-shadow. VTT-disabled → muted grey
  // edge with no accent, so it visibly recedes from the active cards.
  const borderStyle: React.CSSProperties = vttDisabled
    ? {
        border: '1px solid hsl(var(--ac-text-muted) / 0.15)',
        boxShadow: 'var(--ac-elev-1)',
      }
    : isLive
    ? {
        border: '1px solid hsl(var(--ac-mint-primary) / 0.65)',
        boxShadow:
          '0 0 0 1px hsl(var(--ac-mint-primary) / 0.2), 0 0 24px hsl(var(--ac-mint-primary) / 0.25), var(--ac-elev-3)',
      }
    : {
        border: '1px solid hsl(var(--ac-gm-gold) / 0.18)',
        boxShadow:
          'var(--ac-inner-top), inset 0 0 0 1px hsl(var(--ac-gm-gold) / 0.06), var(--ac-elev-3)',
      }

  return (
    <article
      style={{
        position: 'relative',
        borderRadius: 'var(--ac-radius-lg)',
        overflow: 'hidden',
        background: 'hsl(var(--ac-surface-2))',
        display: 'flex',
        flexDirection: 'column',
        transition:
          'transform var(--ac-dur-base) var(--ac-ease-out), box-shadow var(--ac-dur-base) var(--ac-ease-out)',
        // Desaturate + dim the whole card when the VTT is disabled so it
        // reads as "not available here" at a glance.
        filter: vttDisabled ? 'grayscale(1)' : undefined,
        opacity: vttDisabled ? 0.6 : 1,
        ...borderStyle,
      }}
    >
      {/* Image area — 16:9 */}
      <div
        style={{
          position: 'relative',
          aspectRatio: '16 / 9',
          backgroundColor: fallbackColor,
          backgroundImage: game.gameImage ? `url("${game.gameImage}")` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Fallback title when no image — centered, large Balthazar */}
        {!game.gameImage && (
          <div
            className="font-serif-ac"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.5rem',
              textAlign: 'center',
              fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
              lineHeight: 1.15,
              color: 'hsl(var(--ac-text-light) / 0.85)',
              textShadow: '0 1px 2px hsl(0 0% 0% / 0.5)',
            }}
          >
            {game.title}
          </div>
        )}

        {/* Role pill — top right */}
        <span
          style={{
            position: 'absolute',
            top: '0.625rem',
            right: '0.625rem',
            fontSize: '0.5625rem',
            padding: '0.1875rem 0.5rem',
            borderRadius: 'var(--ac-radius-full)',
            background: isGM
              ? 'hsl(var(--ac-gm-gold) / 0.85)'
              : 'hsl(var(--ac-surface-0) / 0.75)',
            color: isGM
              ? 'hsl(var(--ac-text-dark))'
              : 'hsl(var(--ac-text-light))',
            border: isGM
              ? '1px solid hsl(var(--ac-gm-gold))'
              : '1px solid hsl(var(--ac-mint-primary) / 0.3)',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            backdropFilter: 'blur(4px)',
          }}
        >
          {isGM ? 'GM' : 'Player'}
        </span>

        {/* Live pulse — top left, only when active session exists */}
        {isLive && (
          <span
            aria-label="Session in progress"
            style={{
              position: 'absolute',
              top: '0.75rem',
              left: '0.75rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '0.625rem',
              padding: '0.1875rem 0.5rem',
              borderRadius: 'var(--ac-radius-full)',
              background: 'hsl(var(--ac-surface-0) / 0.75)',
              border: '1px solid hsl(var(--ac-mint-primary) / 0.6)',
              color: 'hsl(var(--ac-mint-primary))',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              backdropFilter: 'blur(4px)',
            }}
          >
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'hsl(var(--ac-mint-primary))',
                boxShadow: '0 0 8px hsl(var(--ac-mint-primary))',
              }}
            />
            Live
          </span>
        )}

        {/* Title overlay — bottom gradient band, only when there is an
            actual image (otherwise the centered fallback title owns
            the surface). */}
        {game.gameImage && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              padding: '2rem 0.875rem 0.75rem',
              background:
                'linear-gradient(to top, hsl(var(--ac-surface-0) / 0.92) 20%, hsl(var(--ac-surface-0) / 0.4) 70%, transparent)',
            }}
          >
            <div
              className="font-serif-ac"
              style={{
                fontSize: '1.125rem',
                lineHeight: 1.15,
                color: 'hsl(var(--ac-text-light))',
              }}
            >
              {game.title}
            </div>
          </div>
        )}
      </div>

      {/* Meta + CTA row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          padding: '0.75rem 0.875rem',
          borderTop: '1px solid hsl(var(--ac-gm-gold) / 0.08)',
        }}
      >
        <div
          style={{
            fontSize: '0.6875rem',
            color: 'hsl(var(--ac-text-muted))',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.4rem',
            alignItems: 'center',
            minWidth: 0,
            flex: 1,
          }}
        >
          <span>
            {game.playerCount} {game.playerCount === 1 ? 'player' : 'players'}
          </span>
          {game.nextSession?.scheduledTime && (
            <>
              <span style={{ opacity: 0.4 }}>·</span>
              <span>
                {game.nextSession.sessionNumber != null
                  ? `Session ${game.nextSession.sessionNumber} · `
                  : ''}
                {formatRelativeOrDate(game.nextSession.scheduledTime)}
              </span>
            </>
          )}
          {isLive && (
            <>
              <span style={{ opacity: 0.4 }}>·</span>
              <span style={{ color: 'hsl(var(--ac-mint-primary))' }}>
                In progress
              </span>
            </>
          )}
        </div>
        {/* Gold/gilt CTA — secondary accent. Same family as mint
            (saturated, single-source), different temperature. Built on
            Grimoire Button to inherit pill chrome + focus ring, with
            className overrides for the gold palette which Grimoire
            variants don't cover (the "destructive" variant uses danger
            red; primary is mint; no gold). */}
        <Button
          size="sm"
          variant="outline"
          type="button"
          disabled={vttDisabled || !canLaunch || disabled || launching}
          onClick={onLaunch}
          title={
            vttDisabled
              ? 'This game is not configured for the Arcane Circle tabletop'
              : !canLaunch
                ? 'No upcoming session scheduled'
                : isLive
                  ? 'Join the active session'
                  : 'Launch the next session'
          }
          className={
            vttDisabled
              ? '!border-[hsl(var(--ac-text-muted)/0.3)] !text-[hsl(var(--ac-text-muted))] !bg-transparent !uppercase !tracking-[0.08em] !font-bold'
              : '!border-[hsl(var(--ac-gm-gold)/0.6)] !text-[hsl(var(--ac-gm-gold))] !bg-[hsl(var(--ac-gm-gold)/0.18)] hover:!bg-[hsl(var(--ac-gm-gold)/0.32)] hover:!shadow-[0_0_0_3px_hsl(var(--ac-gm-gold)/0.18)] !uppercase !tracking-[0.08em] !font-bold'
          }
          style={{ flexShrink: 0 }}
        >
          {vttDisabled
            ? 'Tabletop Not Enabled'
            : launching
              ? 'Launching…'
              : isLive
                ? 'Join'
                : 'Launch'}
        </Button>
      </div>
    </article>
  )
}

/**
 * Stable per-game fallback color. Hashes the gameId to a hue, returns
 * a deep, desaturated tile that sits on top of the dark surface
 * without competing with real campaign art elsewhere on the grid.
 */
function gameIdToFallbackColor(gameId: string): string {
  let hash = 0
  for (let i = 0; i < gameId.length; i++) {
    hash = (hash * 31 + gameId.charCodeAt(i)) | 0
  }
  const hue = Math.abs(hash) % 360
  return `hsl(${hue}, 22%, 16%)`
}

function formatRelativeOrDate(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffMin = Math.round(diffMs / 60000)
  const diffHr = Math.round(diffMs / 3600000)
  const diffDay = Math.round(diffMs / 86400000)

  if (diffMs < 0 && diffMs > -3600000) return 'In progress'
  if (diffMin < 60 && diffMin > 0) return `In ${diffMin} min`
  if (diffHr < 24 && diffHr > 0) return `In ${diffHr} hr`
  if (diffDay === 0) return 'Today'
  if (diffDay === 1) return 'Tomorrow'
  if (diffDay > 1 && diffDay < 7) return `In ${diffDay} days`

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: diffDay > 180 ? 'numeric' : undefined,
  })
}
